import { useState, useEffect } from 'react'
import { TrendingUp, Clock, DollarSign, Users, BarChart3, CheckCircle } from 'lucide-react'
import { buildROIStatement } from '@/lib/analytics/time-savings'
import type { AnalyticsSummary } from '@/lib/analytics/time-savings'

interface WorkspaceRow {
  workspace_id: string
  name: string
  client_name?: string
  pre_reads_this_month: number
  hours_saved: number
  last_activity?: number
}

function StatCard({ icon: Icon, label, value, sub }: { icon: typeof TrendingUp; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-border p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        <Icon size={20} strokeWidth={1.75} className="text-echo-indigo opacity-60" />
      </div>
    </div>
  )
}


export function PrincipalDashboard() {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null)
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('echo_session')
    if (!token) { setLoading(false); return }
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    fetch('/api/v1/analytics/principal-summary', { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSummary(data as AnalyticsSummary) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const roi = summary ? buildROIStatement(summary) : 'Your agency ROI will appear here once data is loaded.'

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Agency Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-0.5">All clients · This quarter</p>
        </div>
      </div>

      {loading && (
        <div className="py-8 text-sm text-muted-foreground text-center">Loading dashboard…</div>
      )}

      {/* ROI statement */}
      <div className="rounded-xl border border-echo-indigo/30 bg-echo-indigo/5 p-5">
        <div className="flex items-start gap-3">
          <TrendingUp size={20} strokeWidth={1.75} className="text-echo-indigo mt-0.5" />
          <p className="text-sm text-foreground font-medium">{roi}</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={BarChart3} label="Pre-reads" value={String(summary?.total_pre_reads ?? 0)} sub="this quarter" />
        <StatCard icon={Clock} label="Hours saved" value={`${Math.round(summary?.total_hours_saved ?? 0)}h`} sub="vs. traditional research" />
        <StatCard icon={DollarSign} label="Cost savings" value={`$${Math.round((summary?.total_cost_savings_cents ?? 0) / 100).toLocaleString()}`} sub="@ $150/hr" />
        <StatCard icon={CheckCircle} label="Echo accuracy" value={summary?.accuracy_rate ? `${Math.round(summary.accuracy_rate * 100)}%` : '—'} sub="validated runs" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatCard icon={Users} label="Active clients" value={String(summary?.active_clients ?? 0)} sub="with recent activity" />
        <StatCard icon={BarChart3} label="Pre-reads/day" value={(summary?.pre_reads_per_day ?? 0).toFixed(1)} sub="7-day rolling avg" />
      </div>

      {/* Per-client table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-sm text-foreground">By Client Workspace</h3>
        </div>
        <div className="divide-y divide-border">
          {workspaces.length === 0 && !loading ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No workspaces yet.</div>
          ) : (
            workspaces.map(ws => (
              <div key={ws.workspace_id} className="p-4 flex items-center justify-between hover:bg-surface-subtle/50">
                <div>
                  <p className="text-sm font-medium text-foreground">{ws.client_name ?? ws.name}</p>
                  <p className="text-xs text-muted-foreground">{ws.name}</p>
                </div>
                <div className="flex gap-8 text-right">
                  <div>
                    <p className="text-xs text-muted-foreground">Pre-reads</p>
                    <p className="text-sm font-medium text-foreground">{ws.pre_reads_this_month}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Hours saved</p>
                    <p className="text-sm font-medium text-foreground">{ws.hours_saved}h</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Last activity</p>
                    <p className="text-sm text-foreground">
                      {ws.last_activity ? new Date(ws.last_activity).toLocaleDateString() : '—'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default PrincipalDashboard
